import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Bell, Check, FileText, Layers, CreditCard, Megaphone, AlertCircle, X } from "lucide-react";
import { Link } from "react-router-dom";

const POLL_MS = 30_000;

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function eventIcon(n) {
  const e = n.event;
  if (e === "deck_completed" || e === "deck_failed") return Layers;
  if (e === "slide_completed" || e === "slide_failed") return FileText;
  if (e === "topup_success" || e === "plan_upgraded" || e === "credits_reset") return CreditCard;
  if (e === "credits_low") return AlertCircle;
  if (e === "admin_broadcast") return Megaphone;
  return Bell;
}

function eventTone(n) {
  if (n.event?.endsWith("_failed")) return "text-red-500 bg-red-50";
  if (n.event === "credits_low") return "text-amber-600 bg-amber-50";
  if (n.event === "admin_broadcast") return "text-violet-600 bg-violet-50";
  if (n.type === "billing") return "text-teal-600 bg-teal-50";
  return "text-gray-600 bg-gray-100";
}

export default function NotificationBell({ apiBase }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);
  const btnRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${apiBase}/notifications?limit=30`, {
        withCredentials: true,
        // Polling call — never surface a 5xx toast (we re-poll every
        // POLL_MS and the catch below already handles failures).
        _suppressToast: true,
      });
      setItems(res.data?.notifications || []);
      setUnread(res.data?.unread || 0);
    } catch {
      // silent — auth or network errors shouldn't surface here
    }
  }, [apiBase]);

  useEffect(() => {
    fetchNotifications();
    const t = setInterval(fetchNotifications, POLL_MS);
    const onRefresh = () => fetchNotifications();
    window.addEventListener("notifications:refresh", onRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("notifications:refresh", onRefresh);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const markAllRead = async () => {
    if (!unread) return;
    setLoading(true);
    try {
      await axios.post(`${apiBase}/notifications/mark-read`, { all: true }, { withCredentials: true });
      await fetchNotifications();
    } finally { setLoading(false); }
  };

  const markOneRead = async (id) => {
    try {
      await axios.post(`${apiBase}/notifications/mark-read`, { ids: [id] }, { withCredentials: true });
      // Optimistic update
      setItems(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(u => Math.max(0, u - 1));
    } catch {}
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={() => setOpen(o => !o)}
        className="relative p-1.5 rounded-full hover:bg-gray-100 transition-colors"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-500" />
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center w-[18px] h-[18px] text-[10px] font-bold text-white bg-red-500 rounded-full leading-none"
            data-testid="notification-count"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          className="absolute right-0 mt-2 w-[360px] max-w-[92vw] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
          data-testid="notifications-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-900">Notifications</span>
              {unread > 0 && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 bg-red-500 text-white rounded-full" data-testid="notif-unread-pill">
                  {unread} new
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  disabled={loading}
                  className="text-[11px] text-teal-600 hover:text-teal-700 font-semibold disabled:opacity-50"
                  data-testid="mark-all-read-btn"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
                data-testid="notif-close"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <div className="max-h-[420px] overflow-y-auto" data-testid="notif-list">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-400" data-testid="notif-empty">
                You're all caught up.
              </div>
            ) : (
              items.map(n => {
                const Icon = eventIcon(n);
                const tone = eventTone(n);
                const inner = (
                  <div className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer">
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${tone}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-semibold ${n.read ? "text-gray-600" : "text-gray-900"}`}>
                          {n.title}
                        </p>
                        {!n.read && <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-teal-500" />}
                      </div>
                      <p className={`text-xs mt-0.5 ${n.read ? "text-gray-400" : "text-gray-600"}`}>
                        {n.message}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-1">{relTime(n.created_at)}</p>
                    </div>
                  </div>
                );
                const onActivate = () => {
                  if (!n.read) markOneRead(n.id);
                  if (n.link) setOpen(false);
                };
                return n.link ? (
                  <Link
                    key={n.id}
                    to={n.link}
                    onClick={onActivate}
                    className="block border-b border-gray-50 last:border-b-0"
                    data-testid={`notif-item-${n.id}`}
                  >
                    {inner}
                  </Link>
                ) : (
                  <div
                    key={n.id}
                    onClick={onActivate}
                    className="border-b border-gray-50 last:border-b-0"
                    data-testid={`notif-item-${n.id}`}
                  >
                    {inner}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
